import Pusher from "pusher-js"
import { apiUrl } from "../../lib/api-client"
import { auth } from "../firebase"
import { getStoredLocale } from "../i18n"

let instance: Pusher | null = null

/**
 * Singleton Pusher Channels client for the tab. One websocket connection
 * is shared across every channel subscription. pusher-js handles
 * reconnect/backoff internally.
 *
 * The auth handshake hits our own `/api/common/realtime/auth` endpoint
 * with a fresh Firebase ID token. We use pusher-js v8's `customHandler`
 * (async) so we can await `getIdToken()` — Firebase auto-refreshes the
 * token silently when it's near expiry, avoiding the 1h-stale-token bug
 * that a synchronous `headersProvider` would have.
 */
export function getPusherClient(): Pusher {
	if (instance) return instance
	instance = new Pusher(import.meta.env.VITE_PUSHER_KEY, {
		cluster: import.meta.env.VITE_PUSHER_CLUSTER,
		channelAuthorization: {
			endpoint: apiUrl("/api/common/realtime/auth"),
			transport: "ajax",
			customHandler: async ({ socketId, channelName }, callback) => {
				const user = auth.currentUser
				const token = user ? await user.getIdToken() : null
				try {
					const res = await fetch(apiUrl("/api/common/realtime/auth"), {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							...(token ? { Authorization: `Bearer ${token}` } : {}),
							"Accept-Language": getStoredLocale(),
						},
						body: JSON.stringify({
							socket_id: socketId,
							channel_name: channelName,
						}),
					})
					if (!res.ok) {
						callback(new Error(`Pusher auth failed: ${res.status}`), null)
						return
					}
					const data = await res.json()
					callback(null, data)
				} catch (e) {
					callback(e instanceof Error ? e : new Error(String(e)), null)
				}
			},
		},
	})
	return instance
}
